import {
  Directive,
  ElementRef,
  HostListener,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';

/**
 * Máscara de valor em reais no estilo "caixa": ao digitar, os dígitos entram pela
 * direita e o número vai deslocando para a esquerda (ex.: 1 → 0,01 → 0,12 → 1,23).
 * Apenas dígitos são aceitos; o valor no modelo é em reais (number).
 */
@Directive({
  selector: '[appCurrencyShiftMask]',
})
export class CurrencyShiftMaskDirective implements OnInit, OnDestroy {
  /** Máximo de dígitos (inteiro + decimais). Ex.: 15 = até 999.999.999.999.999,99 */
  private static readonly MAX_DIGITS = 15;

  private valueChangesSubscription?: Subscription;
  private isInputting = false;

  constructor(
    private el: ElementRef,
    private control: NgControl
  ) {}

  ngOnInit(): void {
    const initialValue = this.control.value;
    if (
      initialValue !== null &&
      initialValue !== undefined &&
      initialValue !== ''
    ) {
      setTimeout(() => this.applyDisplay(initialValue), 0);
    }
    this.valueChangesSubscription = this.control.valueChanges?.subscribe(
      (value) => {
        if (
          !this.isInputting &&
          value !== null &&
          value !== undefined &&
          value !== ''
        ) {
          setTimeout(() => this.applyDisplay(value), 0);
        }
      }
    );
  }

  ngOnDestroy(): void {
    this.valueChangesSubscription?.unsubscribe();
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    // Usado principalmente para paste; na digitação o keydown pode ter preventDefault
    this.isInputting = true;
    const input = event.target as HTMLInputElement;
    setTimeout(() => {
      const digits = (input.value.replace(/\D/g, '') || '').slice(
        0,
        CurrencyShiftMaskDirective.MAX_DIGITS
      );
      const valueInReais =
        digits === '' ? null : parseInt(digits, 10) / 100;
      this.control.control?.setValue(valueInReais, { emitEvent: false });
      this.applyDisplay(valueInReais, input);
      this.setCursorToEnd(input);
      this.isInputting = false;
    }, 0);
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const key = event.key;

    if (key === 'Backspace') {
      event.preventDefault();
      const current = this.getDigitsFromValue(this.control.value);
      const newDigits = current.slice(0, -1);
      const valueInReais = newDigits === '' ? null : parseInt(newDigits, 10) / 100;
      this.control.control?.setValue(valueInReais, { emitEvent: false });
      this.applyDisplay(valueInReais, input);
      this.setCursorToEnd(input);
      return;
    }
    if (key === 'Delete') {
      event.preventDefault();
      const current = this.getDigitsFromValue(this.control.value);
      const newDigits = current.slice(1);
      const valueInReais = newDigits === '' ? null : parseInt(newDigits, 10) / 100;
      this.control.control?.setValue(valueInReais, { emitEvent: false });
      this.applyDisplay(valueInReais, input);
      this.setCursorToEnd(input);
      return;
    }
    if (key.length === 1 && /^\d$/.test(key) && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      const current = this.getDigitsFromValue(this.control.value);
      if (current.length >= CurrencyShiftMaskDirective.MAX_DIGITS) return;
      const newDigits = current + key;
      const valueInReais = parseInt(newDigits, 10) / 100;
      this.control.control?.setValue(valueInReais, { emitEvent: false });
      this.applyDisplay(valueInReais, input);
      this.setCursorToEnd(input);
    }
  }

  /** Converte o valor do controle (reais) para string de dígitos (centavos). */
  private getDigitsFromValue(value: number | string | null | undefined): string {
    if (value == null || value === '') return '';
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (isNaN(num) || num < 0) return '';
    return Math.round(num * 100).toString();
  }

  @HostListener('blur', ['$event'])
  onBlur(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = this.control.value;
    if (value !== null && value !== undefined && value !== '') {
      this.applyDisplay(value, input);
    }
  }

  @HostListener('focus', ['$event'])
  onFocus(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = this.control.value;
    if (value !== null && value !== undefined && value !== '') {
      this.applyDisplay(value, input);
    }
    setTimeout(() => this.setCursorToEnd(input), 0);
  }

  private applyDisplay(value: number | null | undefined, input?: HTMLInputElement): void {
    const target = (input || this.el.nativeElement) as HTMLInputElement;
    if (value == null) {
      target.value = '';
      return;
    }
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (isNaN(num)) {
      target.value = '';
      return;
    }
    target.value = new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true,
    }).format(num);
  }

  private setCursorToEnd(input: HTMLInputElement): void {
    requestAnimationFrame(() => {
      const len = input.value.length;
      input.setSelectionRange(len, len);
    });
  }
}
